import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Home, Building2, Landmark, MessageCircle } from "lucide-react";
import { SEO } from "@/components/SEO";
import { PageHero, SectionHeading } from "@/components/SectionHeading";
import { Reveal } from "@/components/Reveal";
import { CTABanner } from "@/components/CTABanner";
import { PRICING_BTO, PRICING_CONDO, TRIP_FEATURES } from "@/data/content";
import { WA_MESSAGES, waLink } from "@/lib/site";

const PriceTable = ({ rows, testid, firstColumn = "Flat Type" }) => (
  <div className="overflow-x-auto rounded-[1.75rem] border border-border bg-white shadow-luxury" data-testid={testid}>
    <table className="w-full min-w-[34rem] text-left">
      <thead>
        <tr className="border-b border-gold/25 bg-cream">
          <th className="px-6 py-4 font-cinzel text-[11px] font-semibold uppercase tracking-[0.2em] text-gold-dark">{firstColumn}</th>
          <th className="px-6 py-4 font-cinzel text-[11px] font-semibold uppercase tracking-[0.2em] text-gold-dark">One-Trip</th>
          <th className="px-6 py-4 font-cinzel text-[11px] font-semibold uppercase tracking-[0.2em] text-gold-dark">Three-Trip</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.type} className={`border-b border-border/70 last:border-0 ${r.popular ? "bg-gold/5" : ""}`}>
            <td className="px-6 py-5">
              <span className="flex items-center gap-2.5 font-serif text-base font-semibold text-charcoal">
                {r.type}
                {r.popular && (
                  <span className="rounded-full bg-gold px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
                    Most popular
                  </span>
                )}
              </span>
              <span className="mt-0.5 block text-xs text-charcoal-mute">{r.size}</span>
            </td>
            <td className="px-6 py-5 font-serif text-xl font-semibold text-charcoal">{typeof r.oneTrip === "number" ? `$${r.oneTrip}` : r.oneTrip}</td>
            <td className="px-6 py-5 font-serif text-xl font-semibold text-gold-dark">{typeof r.threeTrip === "number" ? `$${r.threeTrip}` : r.threeTrip}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function Pricing() {
  const [tab, setTab] = useState("bto");

  return (
    <>
      <SEO
        title="Pricing | BTO & Condo Defect Inspection Rates — Lobang Home Inspection"
        description="Transparent BTO and condo defect inspection pricing in Singapore. One-Trip and Three-Trip options from $108. Landed homes quoted individually. WhatsApp +65 8490 0316."
      />
      <PageHero
        eyebrow="Pricing"
        title="Clear rates for complete peace of mind"
        copy="Choose a single deep-dive visit, or the full three-trip journey with BSC joint inspection and post-rectification verification."
      />

      <section className="px-5 py-16 sm:px-8 sm:py-20" data-testid="pricing-tables-section">
        <div className="mx-auto max-w-5xl">
          <div className="flex justify-center gap-2" role="tablist" aria-label="Property type">
            {[
              { id: "bto", label: "BTO Flats", icon: Home },
              { id: "condo", label: "Condominiums", icon: Building2 },
            ].map(({ id, label, icon: TabIcon }) => (
              <button
                key={id}
                role="tab"
                aria-selected={tab === id}
                data-testid={`pricing-tab-${id}`}
                onClick={() => setTab(id)}
                className={`flex items-center gap-2 rounded-full border px-6 py-3 text-xs font-bold uppercase tracking-wider transition-all duration-300 ${
                  tab === id
                    ? "border-gold bg-gold text-white shadow-luxury"
                    : "border-border bg-white text-charcoal-soft hover:border-gold/50 hover:text-gold-dark"
                }`}
              >
                <TabIcon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>

          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8"
          >
            {tab === "bto" ? <PriceTable rows={PRICING_BTO} testid="pricing-table-bto" /> : <PriceTable rows={PRICING_CONDO} firstColumn="Floor Area" testid="pricing-table-condo" />}
          </motion.div>

          <Reveal className="mt-4 text-center">
            <p className="text-xs text-charcoal-mute" data-testid="pricing-disclaimer">
              All prices in SGD. Every inspection includes a detailed, comprehensive report. POA means price on application.
            </p>
          </Reveal>

          <Reveal delay={0.08}>
            <div className="mt-8 flex flex-col items-center justify-between gap-6 rounded-[1.75rem] bg-charcoal p-8 sm:flex-row sm:p-10" data-testid="landed-inquiry-card">
              <div className="flex items-start gap-4">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-ivory/10 text-gold-light">
                  <Landmark className="h-6 w-6" />
                </span>
                <div>
                  <h3 className="font-serif text-xl font-semibold text-ivory">Landed property?</h3>
                  <p className="mt-1 max-w-md text-sm leading-relaxed text-ivory/60">
                    Terrace, semi-D or bungalow — we tailor the scope and quotation to your total floor area and storey
                    count. Send us your address for a custom quote.
                  </p>
                </div>
              </div>
              <a
                href={waLink(WA_MESSAGES.landed)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="landed-whatsapp-cta"
                className="inline-flex shrink-0 items-center gap-2 rounded-full bg-wa px-6 py-3.5 text-sm font-bold text-white shadow-wa transition-transform duration-300 hover:scale-[1.04]"
              >
                <MessageCircle className="h-4 w-4" />
                Enquire for landed
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream px-5 py-20 sm:px-8 sm:py-24" data-testid="trip-comparison">
        <div className="mx-auto max-w-6xl">
          <SectionHeading
            eyebrow="One-Trip vs Three-Trip"
            title="Which journey fits your handover?"
          />
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {[TRIP_FEATURES.one, TRIP_FEATURES.three].map((t, i) => (
              <Reveal key={t.name} delay={i * 0.1}>
                <div
                  className={`h-full rounded-[1.75rem] p-8 shadow-luxury ${
                    i === 1 ? "bg-charcoal text-ivory" : "border border-border bg-white"
                  }`}
                  data-testid={`trip-card-${i === 0 ? "one" : "three"}`}
                >
                  <p className={`font-cinzel text-[11px] font-semibold uppercase tracking-[0.28em] ${i === 1 ? "text-gold-light" : "text-gold"}`}>
                    {t.tagline}
                  </p>
                  <h3 className={`mt-2 font-serif text-2xl font-semibold ${i === 1 ? "text-ivory" : "text-charcoal"}`}>{t.name}</h3>
                  <ul className="mt-6 space-y-3">
                    {t.features.map((f) => (
                      <li key={f} className={`flex items-start gap-3 text-sm ${i === 1 ? "text-ivory/75" : "text-charcoal-soft"}`}>
                        <CheckCircle2 className={`mt-0.5 h-4 w-4 shrink-0 ${i === 1 ? "text-gold-light" : "text-gold"}`} />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABanner
        title="Lock in your inspection slot."
        copy="WhatsApp us your flat type and key collection date — we'll reply with your exact quote, usually within a few hours."
      />
    </>
  );
}
